import Row from '@sharedComponents/Row';
import SkeletonBlock from '@sharedComponents/SkeletonBlock';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import * as style from './style.module.css';

const Table = ({ columns, items, loading = false, borderless = false, skeletonCount = 8, getRowKey, onSort }) => {
    const wrapperRef = useRef(null);
    const tableRef = useRef(null);
    const hideableColumns = columns.filter((col) => !col.pinned);
    const hasPinnedColumns = hideableColumns.length < columns.length;
    const minVisible = hasPinnedColumns ? 0 : 1;
    const [visibleCount, setVisibleCount] = useState(hideableColumns.length);
    const [resizeTick, setResizeTick] = useState(0);
    const lastGrowTick = useRef(0);
    const columnKey = columns.map((col) => col.key).join('|');

    // A different column set invalidates the count learned for the previous one: the two tabs of
    // the tracking list expose neither the same columns nor the same number of hideable ones, and
    // the results tables add date columns once the data says they have something to show. Declared
    // before the measuring effect so the reset lands in the same commit, ahead of the measurement.
    useLayoutEffect(() => {
        setVisibleCount(hideableColumns.length);
    }, [columnKey, hideableColumns.length]);

    useLayoutEffect(() => {
        const wrapper = wrapperRef.current;
        const table = tableRef.current;
        if (!wrapper || !table) return;
        const overflowing = table.scrollWidth > wrapper.clientWidth;
        if (overflowing && visibleCount > minVisible) {
            setVisibleCount((count) => count - 1);
        } else if (!overflowing && visibleCount < hideableColumns.length && resizeTick !== lastGrowTick.current) {
            lastGrowTick.current = resizeTick;
            setVisibleCount((count) => count + 1);
        }
    }, [visibleCount, resizeTick, minVisible, hideableColumns.length, columns, items, loading]);

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return undefined;
        const observer = new ResizeObserver(() => {
            setResizeTick((tick) => tick + 1);
        });
        observer.observe(wrapper);
        return () => observer.disconnect();
    }, []);

    const visibleHideableKeys = new Set(hideableColumns.slice(0, visibleCount).map((col) => col.key));
    const visibleColumns = columns.filter((col) => col.pinned || visibleHideableKeys.has(col.key));

    // Sizing is only shared out when a column claims the leftover width; otherwise the
    // browser's auto layout is left untouched.
    const hasFillColumn = visibleColumns.some((col) => col.fill);
    const sizingClass = (col) => (hasFillColumn ? (col.fill ? style.fillCol : style.shrinkCol) : '');

    const renderBodyRows = () => {
        if (loading) {
            // The column's own td class gives a loading cell the padding of the real one (an actions cell is
            // tighter than a text cell), and its `skeleton` stands for the content that sets the row height.
            return Array.from({ length: skeletonCount }, (_, i) => (
                <tr key={i} className={style.skeletonRow} data-testid="skeleton-row">
                    {visibleColumns.map((col) => (
                        <td key={col.key} className={`${col.tdClassName ?? style.skeletonTd} ${sizingClass(col)}`}>
                            {col.skeleton ?? <SkeletonBlock height="18px" />}
                        </td>
                    ))}
                </tr>
            ));
        }

        return items.map((item, i) => (
            <Row key={getRowKey ? getRowKey(item) : i}>
                {visibleColumns.map((col) => (
                    <td key={col.key} className={`${col.tdClassName ?? ''} ${sizingClass(col)}`}>
                        {col.render ? col.render(item) : null}
                    </td>
                ))}
            </Row>
        ));
    };

    return (
        <div ref={wrapperRef} className={`${style.wrapper} ${borderless ? style.borderless : ''}`}>
            <table ref={tableRef} className={style.table} data-testid="evaluations-table">
                <thead>
                    <tr>
                        {visibleColumns.map((col) => (
                            <th
                                key={col.key}
                                className={`${style.th} ${sizingClass(col)} ${col.thClassName ?? ''}`}
                                aria-label={col.ariaLabel}
                                data-testid={col.testId}
                            >
                                {col.label}
                                <button type="button" className={style.sortButton} onClick={() => onSort?.(col.key)}>
                                    {col.icon}
                                </button>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>{renderBodyRows()}</tbody>
            </table>
        </div>
    );
};

export default Table;
